import { Card, Stack, Typography } from '@krakentech/coral';
import { Formik, useFormikContext } from 'formik';
import { object, string } from 'yup';

import {
  FormContainer,
  FormTextFieldWithDescription,
  FormSubmitButton,
  AddressFinder,
  FormComponent,
} from '@/components';
import RegistrationStatusAlert from '@/components/auth/RegistrationStatusAlert';
import {
  YourAccountFormV2Props,
  YourAccountFormV2Values,
} from '@/components/forms/YourAccountFormV2/index.types';
import { USER_REGISTRATION_JOURNEY_KEY_OPTIONS } from '@/context/UserRegistrationContext';
import { useUserRegistrationContext } from '@/context/UserRegistrationContext';
import { useAccountIdentification } from '@/hooks/auth/useAccountIdentification';
import { useUserRegistrationStatus } from '@/hooks/auth/useUserRegistrationStatus';
import { useServiceablePropertiesLookup } from '@/hooks/moveHome/useServiceablePropertiesLookup';
import { userRegistrationJourneyConfig } from '@/utils/identifyAndVerify';

type AccountFormV2Props = {
  formBtnIsDisabled: boolean;
  showGenericError?: boolean;
  journeyKey: USER_REGISTRATION_JOURNEY_KEY_OPTIONS;
};

const AccountFormV2 = ({
  showGenericError,
  formBtnIsDisabled,
  journeyKey,
}: AccountFormV2Props) => {
  const {
    setFieldValue,
    values: { address },
  } = useFormikContext<YourAccountFormV2Values>();
  const { surname, scopedToken } = useUserRegistrationContext();
  const {
    data: accountIdentificationData,
    isFetching: accountDataFetching,
    isError: accountDataError,
    isSuccess: accountIdentificationSuccess,
  } = useAccountIdentification({
    familyName: surname,
    scopedToken,
  });
  const {
    data: userRegistrationStatus,
    isFetching: userRegistrationStatusFetching,
    isError: userRegistrationStatusError,
  } = useUserRegistrationStatus({
    scopedToken:
      userRegistrationJourneyConfig[journeyKey].blockForRegisteredEmails &&
      accountIdentificationSuccess
        ? scopedToken
        : '',
  });

  const notEnoughInfoToProceed =
    accountIdentificationSuccess &&
    accountIdentificationData?.blackholeEmailAccountUser &&
    !accountIdentificationData.blackholeEmailAccountUser.hasDateOfBirth &&
    !accountIdentificationData.blackholeEmailAccountUser.hasMobileNumber;

  const canShowNotEnoughInfoAlert =
    userRegistrationJourneyConfig[journeyKey].showDetailedNotEnoughInfoAlert;

  const genericError =
    showGenericError ||
    accountDataError ||
    userRegistrationStatusError ||
    (notEnoughInfoToProceed && !canShowNotEnoughInfoAlert);

  return (
    <FormComponent>
      <FormContainer>
        <Stack gap="lg" direction="vertical">
          <Card>
            <Stack direction="vertical" gap="smMd">
              <Typography variant="h2">Enter your details</Typography>
              <AddressFinder
                query={useServiceablePropertiesLookup}
                title="Your address"
                handleAddressChange={(value) => setFieldValue('address', value)}
                variant="h3"
                defaultSelectedAddress={address}
              />

              <Stack direction="vertical" gap="smMd">
                <Typography variant="h3">Your surname</Typography>

                <FormTextFieldWithDescription
                  label="Your Surname"
                  name="surname"
                  description="Enter your surname as it appears on the bill"
                />
              </Stack>
            </Stack>
          </Card>

          <RegistrationStatusAlert
            journeyKey={journeyKey}
            accountIdentificationData={accountIdentificationData}
            userRegistrationStatus={userRegistrationStatus}
            notEnoughInfoToProceed={
              journeyKey ===
              USER_REGISTRATION_JOURNEY_KEY_OPTIONS.GUEST_APPLY_FOR_METER
                ? notEnoughInfoToProceed && !userRegistrationStatus?.email
                : notEnoughInfoToProceed
            }
            genericError={genericError}
          />

          <FormSubmitButton
            disabled={
              formBtnIsDisabled ||
              (notEnoughInfoToProceed && canShowNotEnoughInfoAlert)
            }
            loading={accountDataFetching || userRegistrationStatusFetching}
          >
            Continue
          </FormSubmitButton>
        </Stack>
      </FormContainer>
    </FormComponent>
  );
};

const YourAccountFormV2 = ({
  billingAddress,
  surname,
  postcode,
  onSubmit,
  showGenericError,
  journeyKey,
}: YourAccountFormV2Props) => (
  <Formik
    initialValues={{
      address: billingAddress,
      surname,
      postcode,
    }}
    validationSchema={object().shape({
      address: object().required('Please select an address'),
      surname: string().required('Please enter your surname'),
      postcode: string().required(''),
    })}
    onSubmit={onSubmit}
  >
    {({ values: { address, surname, postcode } }) => {
      return (
        <AccountFormV2
          showGenericError={showGenericError}
          formBtnIsDisabled={!address?.value || !surname || !postcode}
          journeyKey={journeyKey}
        />
      );
    }}
  </Formik>
);

export default YourAccountFormV2;
